/**
 * Listings Service Layer
 * Optimized queries:
 * - listings (core data including description, spec_links)
 * - listing_ai_data (AI analysis fields)
 * - listing_images (individual image records)
 */

import { supabase } from '@/integrations/supabase/safeClient';

// Lightweight columns for list/card views (~60% smaller than full query)
export const LIGHT_COLUMNS = `
  id, title, price, category, subcategory, item_category, condition,
  location, county, city, township, thumbnail_url,
  likes, views, chats, created_at,
  latitude, longitude, seller_id
`;

// Profile columns for seller info
export const PROFILE_COLUMNS = `
  id, name, county, avatar_url, verified, full_address, bio, username, trust_score
`;

// ============ Interfaces ============

export interface Profile {
  id: string;
  name: string;
  county: string;
  avatar_url?: string;
  verified: boolean;
  full_address?: string;
  bio?: string;
  username?: string;
  trust_score?: number;
}

// Lightweight listing for list/card views
export interface ListingLight {
  id: string;
  title: string;
  price: number;
  category: string;
  subcategory?: string;
  item_category?: string;
  condition?: string;
  location: string;
  county: string;
  city?: string;
  township?: string;
  thumbnail_url?: string;
  images?: string[]; // Optional - may come from demo data
  likes: number;
  views: number;
  chats?: number;
  created_at: string;
  content_updated_at?: string;
  latitude?: number;
  longitude?: number;
  seller_id: string;
  seller: Profile;
  description?: string; // Optional - may come from demo data
}

export interface ListingImage {
  id: string;
  url: string;
  order_index: number;
  is_thumbnail: boolean;
}

export interface ListingAIData {
  ai_category_suggestion?: string;
  ai_confidence_score?: number;
  ai_price_evaluation?: string;
  duplicate_warning?: boolean;
  analyzed_at?: string;
}

// Full listing with all details
export interface ListingFull extends ListingLight {
  images: string[];
  description: string;
  location_type?: string;
  spec_links?: Record<string, string>;
  ai_data?: ListingAIData;
}

// ============ Query Functions ============

/**
 * Fetch listings for list/card views (lightweight query)
 * Returns only essential columns + seller profile
 * Uses status for proper filtering
 */
export async function fetchListingsLight(options?: {
  county?: string;
  category?: string;
  limit?: number;
  offset?: number;
  statusFilter?: ('active' | 'reserved')[];
}): Promise<{ data: ListingLight[]; error: any }> {
  // NOTE: We intentionally widen the Supabase query type here to avoid
  // TS2589 (excessively deep type instantiation) from complex select strings.
  let query: any = (supabase as any)
    .from('listings')
    .select(`
      ${LIGHT_COLUMNS},
      seller:profiles!seller_id(${PROFILE_COLUMNS})
    `)
    .is('deleted_at', null) // Soft delete filter
    .order('created_at', { ascending: false });

  // Filter by status - default to active and reserved
  const allowedStatuses = options?.statusFilter || ['active', 'reserved'];
  // Schema uses `status` (not `status_enum`).
  query = query.in('status', allowedStatuses);

  if (options?.county && options.county !== 'all') {
    query = query.eq('county', options.county);
  }

  if (options?.category) {
    query = query.eq('category', options.category);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 100) - 1);
  }

  const { data, error }: { data: any[] | null; error: any } = await query;

  if (error) {
    console.error('Error fetching listings:', error);
    return { data: [], error };
  }

  // Filter out listings without accessible seller profiles
  const validListings = (data || []).filter(
    (listing: any) => listing.seller !== null
  ) as ListingLight[];

  return { data: validListings, error: null };
}

/**
 * Fetch listings for a specific seller (lightweight query)
 */
export async function fetchListingsLightBySeller(sellerId: string): Promise<{
  data: ListingLight[];
  error: any;
}> {
  const { data, error } = await supabase
    .from('listings')
    .select(`
      ${LIGHT_COLUMNS},
      seller:profiles!seller_id(${PROFILE_COLUMNS})
    `)
    .eq('seller_id', sellerId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching seller listings:', error);
    return { data: [], error };
  }

  return { data: (data || []) as ListingLight[], error: null };
}

/**
 * Fetch full listing details for detail page
 * All fields are now on the listings table; images from listing_images
 */
export async function fetchListingDetails(listingId: string): Promise<{
  data: ListingFull | null;
  error: any;
}> {
  // Fetch listing with all columns + seller
  const { data: listing, error: listingError } = await supabase
    .from('listings')
    .select(`
      *,
      seller:profiles!seller_id(${PROFILE_COLUMNS})
    `)
    .eq('id', listingId)
    .maybeSingle();

  if (listingError || !listing) {
    return { data: null, error: listingError };
  }

  // Fetch images and AI data in parallel
  const [imagesResult, aiDataResult] = await Promise.all([
    supabase
      .from('listing_images')
      .select('id, url, order_index, is_thumbnail')
      .eq('listing_id', listingId)
      .order('order_index', { ascending: true }),
    supabase
      .from('listing_ai_data')
      .select('*')
      .eq('listing_id', listingId)
      .maybeSingle(),
  ]);

  const normalizedImages = imagesResult.data || [];
  const aiData = aiDataResult.data;
  const images = normalizedImages.map((img: ListingImage) => img.url);

  const fullListing: ListingFull = {
    ...listing,
    images,
    description: (listing as any).description || '',
    location_type: listing.location_type,
    spec_links: (listing as any).spec_links as Record<string, string> | undefined,
    ai_data: aiData || undefined,
    seller: listing.seller as Profile,
  };

  return { data: fullListing, error: null };
}

/**
 * Create a new listing with normalized table structure
 */
export async function createListing(data: {
  title: string;
  price: number;
  category: string;
  subcategory?: string;
  item_category?: string;
  condition?: string;
  description: string;
  location: string;
  location_type: string;
  county: string;
  city?: string;
  township?: string;
  latitude?: number;
  longitude?: number;
  images: string[];
  seller_id: string;
  spec_links?: Array<{ title: string; url: string }>; // Optional: reference links for buyers
  imageHashes?: { url: string; hash: string }[]; // Optional: perceptual hashes for scam detection
}): Promise<{ data: { id: string } | null; error: any }> {
  const thumbnailUrl = data.images.length > 0 ? data.images[0] : null;

  // Insert into listings table (description included directly)
  const { data: listing, error: listingError } = await supabase
    .from('listings')
    .insert({
      title: data.title,
      price: data.price,
      category: data.category,
      subcategory: data.subcategory,
      item_category: data.item_category,
      condition: data.condition,
      description: data.description,
      location: data.location,
      location_type: data.location_type,
      county: data.county,
      city: data.city,
      township: data.township,
      latitude: data.latitude,
      longitude: data.longitude,
      thumbnail_url: thumbnailUrl,
      seller_id: data.seller_id,
      spec_links: data.spec_links && data.spec_links.length > 0 ? data.spec_links : null,
    } as any)
    .select('id')
    .single();

  if (listingError || !listing) {
    return { data: null, error: listingError };
  }

  // Insert images into listing_images
  if (data.images.length > 0) {
    const { error: imagesError } = await supabase.from('listing_images').insert(
      data.images.map((url, index) => ({
        listing_id: listing.id,
        url,
        order_index: index,
        is_thumbnail: index === 0,
      }))
    );

    if (imagesError) {
      console.warn('Failed to insert listing_images:', imagesError);
    }
  }

  return { data: { id: listing.id }, error: null };
}

/**
 * Fetch like counts for multiple listings in a single query
 */
export async function fetchLikeCounts(listingIds: string[]): Promise<Map<string, number>> {
  const likesMap = new Map<string, number>();
  
  if (listingIds.length === 0) return likesMap;

  // Use a single query with grouping
  const { data, error } = await supabase
    .from('likes')
    .select('target_id')
    .eq('target_type', 'listing')
    .in('target_id', listingIds);

  if (error) {
    console.error('Error fetching like counts:', error);
    return likesMap;
  }

  // Count likes per listing
  for (const like of data || []) {
    const count = likesMap.get(like.target_id) || 0;
    likesMap.set(like.target_id, count + 1);
  }

  return likesMap;
}
